import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { External } from "@/components/External";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap, type Me } from "@/api";
import { Avatar } from "@/components/Avatar";
import { ErrorNote } from "@/components/ErrorNote";
import { formatDate, pilotName } from "@/lib/format";

export const Route = createFileRoute("/account")({
  component: Account,
});

/** The account page (F414, decision 056): who you are, how you sign in, what you receive, and the way out. */
function Account() {
  const me = useQuery(queries.me());
  if (me.isPending) return null;
  if (!me.data) return <p className="muted">Sign in to see your account.</p>;
  // Keyed on the user: a save refetches `me` and must not drop the drafts.
  return <AccountPage key={me.data.user.id} me={me.data} />;
}

function AccountPage({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const prefs = useQuery(queries.preferences());
  const config = useQuery(queries.config());
  const [firstName, setFirstName] = useState(me.user.first_name);
  const [lastName, setLastName] = useState(me.user.last_name);
  const [handle, setHandle] = useState(me.user.handle);
  const [confirm, setConfirm] = useState("");
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.PATCH("/api/v1/me", {
          body: { first_name: firstName.trim(), last_name: lastName.trim(), handle: handle.trim() },
        }),
      ),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queries.me().queryKey, fresh);
      setHandle(fresh.user.handle);
    },
  });
  const setEmail = useMutation({
    mutationFn: async (on: boolean) => unwrap(await api.PATCH("/api/v1/me/preferences", { body: { email_notifications: on } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["preferences"] }),
  });
  const setCrewNamed = useMutation({
    mutationFn: async (on: boolean) => unwrap(await api.PATCH("/api/v1/me/preferences", { body: { crew_named: on } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["preferences"] }),
  });
  // The picture is its own switch (decision 125), apart from the name shown.
  const setPictureShown = useMutation({
    mutationFn: async (on: boolean) => unwrap(await api.PATCH("/api/v1/me/preferences", { body: { picture_shown: on } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["preferences"] }),
  });
  // What others see of you (F498, decision 105): the two choices the
  // platform applies wherever you appear.
  const setNameShown = useMutation({
    mutationFn: async (name_shown: "full" | "short" | "handle") => unwrap(await api.PATCH("/api/v1/me/preferences", { body: { name_shown } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["preferences"] }),
  });
  const setSeenBy = useMutation({
    mutationFn: async (seen_by: "members" | "managers") => unwrap(await api.PATCH("/api/v1/me/preferences", { body: { seen_by } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["preferences"] }),
  });
  const remove = useMutation({
    mutationFn: async () => unwrap(await api.DELETE("/api/v1/me", { body: { handle: confirm.trim() } })),
    onSuccess: () => {
      // As the sign-out does: the session set to null for every observer,
      // everything else dropped (a cleared cache would leave the shell
      // watching a removed entry).
      queryClient.setQueryData(queries.me().queryKey, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      navigate({ to: "/" });
    },
  });
  const fullName = pilotName(me.user);
  const shortName = me.user.first_name && me.user.last_name ? `${me.user.first_name} ${me.user.last_name.slice(0, 1).toUpperCase()}.` : fullName;
  return (
    <div className="space-y-5">
      <header>
        <h1 className="page-title">Account & privacy</h1>
        <p className="muted text-sm">Who you are on the platform, what the other pilots see of you, how you are reached, and what you can take away.</p>
      </header>
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <section className="card space-y-4">
            <div className="flex items-center gap-4">
              <Avatar handle={me.user.handle} name={fullName} url={me.user.avatar_url} large />
              <div className="flex flex-col gap-0.5">
                <span className="text-lg font-semibold" data-testid="account-name">
                  {fullName}
                </span>
                <span className="muted text-[0.9286rem]">
                  @{me.user.handle} · member since {formatDate(me.user.created_at)}
                </span>
              </div>
              {me.in_crew_room ? (
                <span className="pill pill-ok" data-testid="crew-room-pill">
                  Crew room
                </span>
              ) : null}
              {me.user.is_admin ? <span className="pill pill-warn ml-auto">Admin</span> : null}
            </div>
            <form
              className="space-y-3 border-t border-line pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
              data-testid="profile-form"
            >
              <h2 className="text-[1.0714rem] font-semibold">Profile</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="first-name">
                    First name
                  </label>
                  <input id="first-name" className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={60} required data-testid="first-name" />
                </div>
                <div>
                  <label className="label" htmlFor="last-name">
                    Last name
                  </label>
                  <input id="last-name" className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={60} required data-testid="last-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="handle">
                    Handle
                  </label>
                  <input id="handle" className="input font-mono" value={handle} onChange={(e) => setHandle(e.target.value)} maxLength={32} data-testid="handle" />
                  <p className="muted mt-1 text-xs">Shown to other pilots in the logbooks and the cockpit window. Lowercase letters, digits, dots, dashes and underscores, 2 to 32 characters.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" className="btn btn-primary" disabled={save.isPending} data-testid="save-profile">
                  Save
                </button>
                {save.isSuccess ? (
                  <span className="text-ok text-sm" data-testid="profile-saved">
                    Saved.
                  </span>
                ) : null}
                <span className="muted text-xs">Who sees your name is your choice, under Privacy below.</span>
              </div>
              <ErrorNote error={save.error} />
            </form>
          </section>
          <section className="card space-y-2">
            <h2 className="text-[1.0714rem] font-semibold">Sign-in</h2>
            <div className="flex items-center gap-3 border-t border-line pt-3">
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold">Discord</span>
                <span className="muted text-xs">
                  The only way in. {me.email ? `E-mail from Discord: ${me.email}.` : "Discord shared no e-mail address."} The companion signs in with the same account.
                </span>
              </div>
              <span className="text-ok ml-auto text-xs">Connected</span>
            </div>
          </section>
          <section className="card space-y-4" data-testid="privacy-prefs" id="privacy">
            <div>
              <h2 className="text-[1.0714rem] font-semibold">Privacy</h2>
              <p className="muted text-xs">What the other pilots see of you. The platform applies these choices everywhere you appear: the bookings, the live map, the members, the logbooks, the history, the tech log. You always see yourself, and the administrators see everyone.</p>
            </div>
            {prefs.data ? (
              <>
                <fieldset className="space-y-2 border-t border-line pt-3" data-testid="name-shown-pref">
                  <legend className="font-semibold text-[0.9286rem]">Your name</legend>
                  <label className="flex items-start gap-2 text-[0.9286rem]">
                    <input type="radio" name="name-shown" className="mt-1" checked={prefs.data.name_shown === "full"} onChange={() => setNameShown.mutate("full")} disabled={setNameShown.isPending} data-testid="name-shown-full" />
                    <span>
                      Your full name
                      <span className="muted block text-xs">{fullName}. A long name is printed as the short form.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-[0.9286rem]">
                    <input type="radio" name="name-shown" className="mt-1" checked={prefs.data.name_shown === "short"} onChange={() => setNameShown.mutate("short")} disabled={setNameShown.isPending} data-testid="name-shown-short" />
                    <span>
                      Your first name and the initial of your last
                      <span className="muted block text-xs">{shortName}.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-[0.9286rem]">
                    <input type="radio" name="name-shown" className="mt-1" checked={prefs.data.name_shown === "handle"} onChange={() => setNameShown.mutate("handle")} disabled={setNameShown.isPending} data-testid="name-shown-handle" />
                    <span>
                      Your handle alone
                      <span className="muted block text-xs">@{me.user.handle}. Your first and last name stay on your account.</span>
                    </span>
                  </label>
                  <ErrorNote error={setNameShown.error} />
                </fieldset>
                <div className="space-y-2 border-t border-line pt-3" data-testid="picture-prefs">
                  <h3 className="font-semibold text-[0.9286rem]">Your picture</h3>
                  <label className="flex items-center justify-between gap-3 text-[0.9286rem]">
                    <span>Show my Discord picture beside my name</span>
                    <input type="checkbox" checked={prefs.data.picture_shown} onChange={(e) => setPictureShown.mutate(e.target.checked)} disabled={setPictureShown.isPending} data-testid="picture-shown-pref" />
                  </label>
                  <ErrorNote error={setPictureShown.error} />
                </div>
                <fieldset className="space-y-2 border-t border-line pt-3" data-testid="seen-by-pref">
                  <legend className="font-semibold text-[0.9286rem]">Who sees you</legend>
                  <label className="flex items-start gap-2 text-[0.9286rem]">
                    <input type="radio" name="seen-by" className="mt-1" checked={prefs.data.seen_by === "members"} onChange={() => setSeenBy.mutate("members")} disabled={setSeenBy.isPending} data-testid="seen-by-members" />
                    <span>
                      Every member of the aircraft you share
                      <span className="muted block text-xs">They see who has the aircraft, who flew it, who wrote in the tech log.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-[0.9286rem]">
                    <input type="radio" name="seen-by" className="mt-1" checked={prefs.data.seen_by === "managers"} onChange={() => setSeenBy.mutate("managers")} disabled={setSeenBy.isPending} data-testid="seen-by-managers" />
                    <span>
                      The owner and the captains only
                      <span className="muted block text-xs">They answer for the aircraft and always know who has it. To the other members you are "a pilot", with no name, no picture and no link. An aircraft you own always names you as its owner.</span>
                    </span>
                  </label>
                  <ErrorNote error={setSeenBy.error} />
                </fieldset>
                <div className="space-y-2 border-t border-line pt-3" data-testid="crew-prefs">
                  <h3 className="font-semibold text-[0.9286rem]">Shared cockpit</h3>
                  <p className="muted text-xs">In a shared cockpit the crew's seats are shown as small icons on the flight's card. Your name goes with yours on hover when you let it; off, the icon says a pilot who keeps their name private.</p>
                  <label className="flex items-center justify-between gap-3 text-[0.9286rem]">
                    <span>Show my name to the others</span>
                    <input type="checkbox" checked={prefs.data.crew_named} onChange={(e) => setCrewNamed.mutate(e.target.checked)} disabled={setCrewNamed.isPending} data-testid="crew-named-pref" />
                  </label>
                  <ErrorNote error={setCrewNamed.error} />
                </div>
              </>
            ) : null}
          </section>
          <section className="card space-y-3 border-bad/50">
            <h2 className="text-[1.0714rem] font-semibold text-bad">Leave the platform</h2>
            <p className="text-[0.9286rem] text-ink-3">
              Deleting the account signs you out everywhere and removes your name, handle, e-mail and Discord link. The history of the airframes you flew stays whole, attributed to a removed
              pilot. Hand over or retire the airframes you own first; an aircraft you have must be returned.
            </p>
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                remove.mutate();
              }}
            >
              <input
                className="input font-mono"
                style={{ width: 220 }}
                placeholder={`type ${me.user.handle} to confirm`}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-label="Type your handle to confirm"
                data-testid="confirm-handle"
              />
              <button type="submit" className="btn btn-danger" disabled={remove.isPending || confirm.trim().toLowerCase() !== me.user.handle.toLowerCase()} data-testid="delete-account">
                Delete my account
              </button>
            </form>
            <ErrorNote error={remove.error} />
          </section>
        </div>
        <div className="space-y-4">
          <section className="card space-y-3" data-testid="crew-room">
            <h2 className="text-[1.0714rem] font-semibold">The crew room</h2>
            {me.in_crew_room ? (
              <p className="muted text-sm">You are in the ShariFly Discord server: bugs, questions and the word on new versions go there. The badge follows your membership at each sign-in.</p>
            ) : (
              <p className="muted text-sm">The pilots talk on the ShariFly Discord server: bugs, questions, the word on new versions. Join it, sign in again, and the badge appears here and the Verified role on Discord.</p>
            )}
            {config.data?.discord_invite ? (
              <External href={config.data.discord_invite} className="btn btn-sm">
                {me.in_crew_room ? "Open Discord" : "Join the server"}
              </External>
            ) : null}
          </section>
          <section className="card space-y-3">
            <h2 className="text-[1.0714rem] font-semibold">Notifications</h2>
            <p className="muted text-xs">Requests, approvals, bookings, returns and held returns on your airframes reach you under the bell at the top; the Fleet's Activity list keeps the ones you read.</p>
            {prefs.data ? (
              <label className="flex items-center justify-between gap-3 text-[0.9286rem]">
                <span>Also by e-mail</span>
                <input type="checkbox" checked={prefs.data.email_notifications} onChange={(e) => setEmail.mutate(e.target.checked)} disabled={setEmail.isPending || !prefs.data.has_email} data-testid="email-pref" />
              </label>
            ) : null}
            {prefs.data && !prefs.data.has_email ? <p className="muted text-xs">Discord shared no e-mail address, so there is nowhere to send them.</p> : null}
            <ErrorNote error={setEmail.error} />
          </section>
          <section className="card space-y-2">
            <h2 className="text-[1.0714rem] font-semibold">Your data</h2>
            <p className="muted text-xs">
              The platform holds your profile, your memberships, your bookings and the flights and events you reported.{" "}
              <Link to="/privacy" className="underline" data-testid="account-data-link">
                Read how your data is kept
              </Link>
              , who sees it and how to take it away.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
